import { useCallback, useEffect, useRef, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCopy } from '@fortawesome/pro-solid-svg-icons/faCopy';
import { EVENTS } from 'shared/constants';
import { MPOG_INDICATOR_FIELDS, MPOG_LINK } from 'shared/constants/quality-metric.constants';
import { formatTitleCase } from 'shared/utils';
import { useDateFormatter } from 'shared/hooks/dates.hooks';
import analyticsService from 'src/helpers/analytics.service';
import { CopyToClipboard } from 'src/components/common/copy-to-clipboard';
import { Tooltip } from 'src/components/core';
import styles from './case-detail.module.scss';

function TruncatableValue({ value, lang }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const el = textRef.current;
    if (el) {
      setIsTruncated(el.scrollHeight > el.clientHeight);
    }
  }, [value]);

  return (
    <>
      <div
        ref={textRef}
        className={`${styles.infoValue} ${!expanded ? styles.infoValueTruncated : ''}`}>
        {value}
      </div>
      {isTruncated && (
        <button
          type="button"
          className={styles.infoShowMore}
          onClick={() => setExpanded(!expanded)}>
          {expanded ? lang.SHOW_LESS : lang.SHOW_MORE}
        </button>
      )}
    </>
  );
}

function isMpogCase(metadata) {
  if (!metadata) return false;
  return MPOG_INDICATOR_FIELDS.some(field => metadata[field] != null);
}

export function CaseInfoColumn({ caseData, lang }) {
  const { formatLongDateTime } = useDateFormatter();

  const handleExternalLinkClick = useCallback(
    link => {
      analyticsService.track(EVENTS.QUALITY_METRICS.EXTERNAL_LINK_CLICK, {
        link,
        caseId: caseData?.externalId,
        title: caseData?.metadata?.procedure,
      });
    },
    [caseData],
  );

  const handleCopy = useCallback(() => {
    analyticsService.track(EVENTS.QUALITY_METRICS.COPY, {
      caseId: caseData?.externalId,
      title: caseData?.metadata?.procedure,
    });
  }, [caseData]);

  const fields = useMemo(() => {
    if (!caseData) return [];

    const metadata = caseData.metadata || {};

    return [
      {
        key: 'externalResource',
        label: lang.CASE_EXTERNAL_RESOURCE,
        render: () => {
          const mpogLink = MPOG_LINK.replace('{caseId}', caseData.externalId);
          return (
            <a
              href={mpogLink}
              target="_blank"
              rel="noopener noreferrer"
              className={styles.infoLink}
              onClick={() => handleExternalLinkClick(mpogLink)}>
              {lang.VIEW_IN_MPOG}
            </a>
          );
        },
        visible: isMpogCase(metadata) && !!caseData.externalId,
      },
      {
        key: 'externalId',
        label: lang.CASE_EXTERNAL_ID,
        value: caseData.externalId,
        copyable: true,
      },
      {
        key: 'date',
        label: lang.CASE_DATE,
        value: caseData.date ? formatLongDateTime(caseData.date) : null,
      },
      {
        key: 'service',
        label: lang.CASE_SERVICE,
        value: formatTitleCase(metadata.service),
      },
      {
        key: 'procedure',
        label: lang.CASE_PROCEDURE,
        value: formatTitleCase(metadata.procedure),
        truncatable: true,
      },
      {
        key: 'admissionType',
        label: lang.CASE_ADMISSION_TYPE,
        value: formatTitleCase(metadata.admissionType),
      },
      {
        key: 'location',
        label: lang.CASE_LOCATION,
        value: formatTitleCase(metadata.location),
      },
      {
        key: 'room',
        label: lang.CASE_ROOM,
        value: formatTitleCase(metadata.room),
      },
      {
        key: 'type',
        label: lang.CASE_ANESTHESIA_TYPE,
        value: formatTitleCase(metadata.type),
      },
    ].filter(item => item.visible !== false && (item.value || item.render));
  }, [caseData, lang, formatLongDateTime, handleExternalLinkClick]);

  return (
    <div className={styles.column}>
      <div className={styles.columnHeader}>{lang.TAB_CASE_INFO}</div>
      <div className={styles.columnContent}>
        {fields.map(field => (
          <div key={field.key} className={styles.infoRow}>
            <div>
              <div className={styles.infoLabel}>{field.label}</div>
              {field.render ? (
                field.render()
              ) : field.truncatable ? (
                <TruncatableValue value={field.value} lang={lang} />
              ) : (
                <div className={styles.infoValue}>{field.value}</div>
              )}
            </div>
            {field.copyable && field.value && (
              <Tooltip title={lang.COPY_CASE_ID}>
                <span>
                  <CopyToClipboard
                    text={field.value}
                    onCopy={handleCopy}
                    className={styles.copyButton}>
                    <FontAwesomeIcon icon={faCopy} size="xl" />
                  </CopyToClipboard>
                </span>
              </Tooltip>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
